import { useMemo } from 'react';

import { toTitleCase } from 'core/Utils';
import { Badge, BadgeProps, Group, Stack, Text, ThemeIcon } from 'ui';
import { IconBookmarkHeart } from 'ui/Icons';

import { EntryElement } from '../../../../hooks/queries/Ledger/types';
import { absCentsToDollar } from '../../../../utils/centsToDollar';

const STATUS_BADGE_MAP = new Map<string, BadgeProps>([
  [
    'complete',
    {
      children: 'Complete',
      variant: 'filled',
      type: 'success',
    },
  ],
  [
    'pending',
    {
      children: 'Pending',
      variant: 'filled',
      type: 'info',
    },
  ],
  [
    'pending_payment',
    {
      children: 'Pending ',
      variant: 'filled',
      type: 'info',
    },
  ],
  [
    'failed',
    {
      children: 'Failed',
      variant: 'filled',
      type: 'error',
    },
  ],
  [
    'approval_needed',
    {
      children: 'Approval Needed',
      variant: 'filled',
      type: 'neutral',
    },
  ],
]);

const LinkedEntryCard = ({ entry }: { entry: EntryElement }) => {
  const isDonation = entry.owner_type === 'Cause';
  const titleText = useMemo(() => {
    if (isDonation) {
      return `${entry.payload?.core?.cause?.name ?? 'Cause'} Donation Amount`;
    }
    return 'Givebacks Wallet';
  }, [entry.payload?.core?.cause?.name, isDonation]);

  const subText = useMemo(() => {
    const amountText = [
      entry.split != null ? `${entry.split}%` : '',
      absCentsToDollar(entry.amount),
    ]
      .filter(Boolean)
      .join(' or ');
    return `${isDonation ? 'Donated' : 'Transferred'} ${amountText}`;
  }, [entry.amount, entry.split, isDonation]);

  return (
    <Stack gap="xs">
      <Group gap="xs" align="center">
        {isDonation && (
          <ThemeIcon variant="transparent" size="sm" c="yellow">
            <IconBookmarkHeart />
          </ThemeIcon>
        )}
        <Text fz={14} fw={600}>
          {titleText}
        </Text>
      </Group>
      <Group justify="space-between">
        <Text fz={14} c="gray">
          {subText}
        </Text>
        <Badge {...STATUS_BADGE_MAP.get(entry.status)}>{toTitleCase(entry.status)}</Badge>
      </Group>
    </Stack>
  );
};

export { LinkedEntryCard };
